import type { SyntheticEvent } from 'react';

/**
 * PUT a blob to a presigned URL with a progress-stall guard: aborts when no
 * bytes move for STALL_MS (default 45s). Cross-border uploads to mainland
 * COS can silently wedge; a stalled upload must FAIL (so the caller can fall
 * back) rather than hang a publish forever.
 */
export function putWithStallGuard(
  url: string,
  blob: Blob,
  contentType: string,
  stallMs = 45_000,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stallTimer: ReturnType<typeof setTimeout>;
    const resetStall = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => xhr.abort(), stallMs);
    };
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      resetStall();
      if (onProgress && e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      clearTimeout(stallTimer);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`upload failed (${xhr.status})`));
    };
    xhr.onerror = () => {
      clearTimeout(stallTimer);
      reject(new Error('upload network error'));
    };
    xhr.onabort = () => {
      clearTimeout(stallTimer);
      reject(new Error('upload stalled'));
    };
    resetStall();
    xhr.send(blob);
  });
}

/**
 * MediaRecorder WebM published before the fix-webm-duration stamp has no
 * duration in its header — the player reports Infinity and the seek bar can't
 * map position to time. Standard repair: seek far past the end; the browser
 * scans the file, learns the real duration, then we snap back to the start.
 * Wire as onLoadedMetadata on any <video> that may play a recorded clip.
 */
export function repairInfiniteDuration(e: SyntheticEvent<HTMLVideoElement>): void {
  const video = e.currentTarget;
  if (video.duration !== Infinity) return;
  const onTimeUpdate = () => {
    video.removeEventListener('timeupdate', onTimeUpdate);
    video.currentTime = 0;
  };
  video.addEventListener('timeupdate', onTimeUpdate);
  video.currentTime = 1e10;
}
